import { config } from '@iheartjane/config';
import type {
  StrongholdCustomer,
  StrongholdIntegration,
  StrongholdPaymentSource,
} from '@iheartjane/data-access';

interface StrongholdPayClient {
  addPaymentSource: (
    customerToken: string,
    options: {
      onError: (error: string) => void;
      onExit: () => void;
      onReady: () => void;
      onSuccess: (paymentSource: StrongholdPaymentSource) => void;
    }
  ) => void;
}

declare global {
  interface Window {
    /** @see https://docs.strongholdpay.com/docs/stronghold-pay */
    Stronghold?: {
      Pay: (clientOptions: {
        environment: 'live' | 'sandbox';
        integrationId: string;
        publishableKey: string;
      }) => StrongholdPayClient;
    };
  }
}

interface Props {
  adjustStrongholdIframeHeight?: boolean;
  onClose: () => void;
  onError: (error: Error | string) => void;
  onSuccess: (paymentSource: StrongholdPaymentSource) => void;
  strongholdCustomer?: StrongholdCustomer | null;
  strongholdIntegration?: StrongholdIntegration | null;
}

export const loadStrongholdWidget = ({
  adjustStrongholdIframeHeight = false,
  onClose,
  onError,
  onSuccess,
  strongholdCustomer,
  strongholdIntegration,
}: Props) => {
  if (!window.Stronghold?.Pay) {
    onError(new Error('No Stronghold.Pay.JS'));
    return;
  }

  if (!strongholdCustomer) {
    onError(new Error('No Stronghold customer'));
    return;
  }

  if (!strongholdIntegration) {
    onError(new Error('No Stronghold integration'));
    return;
  }

  document.body.style.overflow = 'hidden';

  const strongholdPay = window.Stronghold.Pay({
    publishableKey: strongholdIntegration.publishable_key,
    environment: config.strongholdEnv,
    integrationId: config.strongholdIntegrationId,
  });

  const onReady = () => {
    if (!adjustStrongholdIframeHeight) {
      return;
    }

    const iframe = document.querySelector(
      '#stronghold-pay-iframe'
    ) as HTMLElement;

    if (iframe) {
      iframe.style.maxHeight = '608px';
    }
  };

  const onExit = () => {
    onClose && onClose();
  };

  strongholdPay.addPaymentSource(strongholdCustomer.token.token, {
    onReady,
    onSuccess: (paymentSource: StrongholdPaymentSource) => {
      onSuccess(paymentSource);
    },
    onExit,
    onError: (error: string) => onError(error),
  });
};
